import axios from "axios";

const _rawBase = (
  process.env.REACT_APP_API_URL || "http://localhost:4001"
).replace(/\/api\/v1\/?$/, "");

// Create axios instance with default config
const atsApi = axios.create({
  baseURL: `${_rawBase}/api/v1/ats`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
atsApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor for error handling
atsApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// Application Management
export const applicationService = {
  // Get all applications for a requisition
  getApplications: (requisitionId, params = {}) => {
    return atsApi.get(`/applications/${requisitionId}`, { params });
  },

  // Get single application
  getApplication: (id) => {
    return atsApi.get(`/applications/detail/${id}`);
  },

  // Get next/previous allowed stages for an application
  getNextStages: (id) => {
    return atsApi.get(`/applications/${id}/next-stages`);
  },

  // Create new application
  createApplication: (data) => {
    return atsApi.post("/applications", data);
  },

  // Update application
  updateApplication: (id, data) => {
    return atsApi.put(`/applications/${id}`, data);
  },

  // Update application stage
  updateApplicationStage: (id, data) => {
    return atsApi.put(`/applications/${id}/stage`, data);
  },

  // Add interview to application
  addInterview: (id, interviewData) => {
    return atsApi.post(`/applications/${id}/interviews`, interviewData);
  },

  // Update interview feedback
  updateInterviewFeedback: (id, interviewIndex, feedback) => {
    return atsApi.put(
      `/applications/${id}/interviews/${interviewIndex}/feedback`,
      feedback,
    );
  },

  // Bulk update applications
  bulkUpdateApplications: (data) => {
    return atsApi.put("/applications/bulk", data);
  },

  // Send email to candidate
  sendEmail: (id, emailData) => {
    return atsApi.post(`/applications/${id}/send-email`, emailData);
  },
};

// Resume Management
export const resumeService = {
  // Upload resume
  uploadResume: (formData) => {
    return atsApi.post("/resumes/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Get resume by ID
  getResume: (id) => {
    return atsApi.get(`/resumes/${id}`);
  },
};

// Workflow Management
export const workflowService = {
  // Get workflows
  getWorkflows: (params = {}) => {
    return atsApi.get("/workflows", { params });
  },

  // Create workflow
  createWorkflow: (data) => {
    return atsApi.post("/workflows", data);
  },
};

// Dashboard & Analytics
export const dashboardService = {
  // Get dashboard statistics
  getDashboardStats: (params = {}) => {
    return atsApi.get("/dashboard/stats", { params });
  },

  // Get time-to-hire analytics
  getTimeToHireAnalytics: (params = {}) => {
    return atsApi.get("/analytics/time-to-hire", { params });
  },

  // Get source effectiveness analytics
  getSourceEffectivenessAnalytics: (params = {}) => {
    return atsApi.get("/analytics/source-effectiveness", { params });
  },

  // Get interviewer performance analytics
  getInterviewerPerformanceAnalytics: (params = {}) => {
    return atsApi.get("/analytics/interviewer-performance", { params });
  },
};

// Audit & Compliance
export const auditService = {
  // Get audit logs
  getAuditLogs: (params = {}) => {
    return atsApi.get("/audit", { params });
  },

  // Get GDPR compliance report
  getGDPRReport: (params = {}) => {
    return atsApi.get("/compliance/gdpr-report", { params });
  },

  // Request data deletion
  requestDataDeletion: (data) => {
    return atsApi.post("/compliance/data-deletion", data);
  },
};

// Integration with Sourcing System
export const integrationService = {
  // Get candidates from sourcing system for a job
  getCandidatesForJob: (requisitionId) => {
    return atsApi.get(`/integrations/candidates/${requisitionId}`);
  },

  // Import candidate from sourcing system
  importCandidate: (data) => {
    return atsApi.post("/integrations/import-candidate", data);
  },
};

// Utility functions
export const atsUtils = {
  // Format application status for display
  formatApplicationStatus: (status) => {
    const statusMap = {
      applied: "Applied",
      screening: "Screening",
      phone_screen: "Phone Screen",
      assessment: "Assessment",
      interview_1: "1st Interview",
      interview_2: "2nd Interview",
      interview_3: "3rd Interview",
      final_interview: "Final Interview",
      offer_pending: "Offer Pending",
      offer_accepted: "Offer Accepted",
      offer_rejected: "Offer Rejected",
      hired: "Hired",
      rejected: "Rejected",
      withdrawn: "Withdrawn",
      on_hold: "On Hold",
    };
    return statusMap[status] || status;
  },

  // Get status color for UI
  getStatusColor: (status) => {
    const colorMap = {
      applied: "blue",
      screening: "yellow",
      phone_screen: "orange",
      assessment: "purple",
      interview_1: "cyan",
      interview_2: "teal",
      interview_3: "green",
      final_interview: "emerald",
      offer_pending: "indigo",
      offer_accepted: "green",
      offer_rejected: "red",
      hired: "green",
      rejected: "red",
      withdrawn: "gray",
      on_hold: "yellow",
    };
    return colorMap[status] || "gray";
  },

  // Calculate days since application
  getDaysSinceApplication: (appliedAt) => {
    const appliedDate = new Date(appliedAt);
    const today = new Date();
    const diffTime = Math.abs(today - appliedDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  },

  // Format interview date and time
  formatInterviewDateTime: (date, time) => {
    const interviewDate = new Date(date);
    return interviewDate.toLocaleDateString() + (time ? ` at ${time}` : "");
  },
};

export default atsApi;
